import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Monitor, Plus, Trash2, Copy, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

// Sortable Row Component
const SortableRow = ({ id, children, className = '' }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? 'bg-muted/50' : ''}`}
    >
      <TableCell className="w-8 p-1">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
};

const VIDEO_CONNECTORS = ['HDMI', 'SDI', 'DisplayPort', 'USB-C', 'VGA', 'NDI', 'Other'];
const RESOLUTIONS = ['1080p', '4K', '720p', 'Other'];

const EditableVideoTab = ({ event, onUpdate }) => {
  const [videoOverview, setVideoOverview] = useState(event?.video_overview || '');
  const [videoNotes, setVideoNotes] = useState(event?.video_notes || '');
  const [videoInputs, setVideoInputs] = useState(event?.video_inputs || []);
  const [videoOutputs, setVideoOutputs] = useState(event?.video_outputs || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, index: null, item: null });

  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setVideoOverview(event?.video_overview || '');
      setVideoNotes(event?.video_notes || '');
      // Ensure all items have IDs for drag-and-drop, filter out null items
      const ensureIds = (items, prefix) => (items || [])
        .filter(item => item != null)
        .map((item, idx) => ({
          ...item,
          id: item.id || `${prefix}-${Date.now()}-${idx}`
        }));
      setVideoInputs(ensureIds(event?.video_inputs, 'vinput'));
      setVideoOutputs(ensureIds(event?.video_outputs, 'voutput'));
      isInitialMount.current = false;
    }
  }, [event?.video_overview, event?.video_notes, event?.video_inputs, event?.video_outputs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-number items after reorder
  const renumberItems = (items, numberField) => {
    return items.map((item, index) => ({
      ...item,
      [numberField]: String(index + 1),
    }));
  };

  // Handle drag end for inputs
  const handleInputDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const validInputs = videoInputs.filter(item => item?.id);
      const oldIndex = validInputs.findIndex(item => item.id === active.id);
      const newIndex = validInputs.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validInputs, oldIndex, newIndex), 'input_number');
        setVideoInputs(reordered);
        queueChange({ video_inputs: reordered });
        saveNow();
      }
    }
  };

  // Handle drag end for outputs
  const handleOutputDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const validOutputs = videoOutputs.filter(item => item?.id);
      const oldIndex = validOutputs.findIndex(item => item.id === active.id);
      const newIndex = validOutputs.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validOutputs, oldIndex, newIndex), 'output_number');
        setVideoOutputs(reordered);
        queueChange({ video_outputs: reordered });
        saveNow();
      }
    }
  };

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change
  const queueChange = useCallback((updates) => {
    pendingChanges.current = { ...pendingChanges.current, ...updates };
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleOverviewChange = (value) => {
    setVideoOverview(value);
    queueChange({ video_overview: value });
  };

  const handleNotesChange = (value) => {
    setVideoNotes(value);
    queueChange({ video_notes: value });
  };

  // Video Inputs
  const addVideoInput = () => {
    const newItem = {
      id: `input-${Date.now()}`,
      input_number: String(videoInputs.length + 1),
      description: '',
      source: '',
      resolution: '',
      video_connector: '',
      location: '',
      notes: ''
    };
    const newInputs = [...videoInputs, newItem];
    setVideoInputs(newInputs);
    queueChange({ video_inputs: newInputs });
  };

  const updateVideoInput = (index, field, value) => {
    const newInputs = [...videoInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setVideoInputs(newInputs);
    queueChange({ video_inputs: newInputs });
  };

  const removeVideoInput = (index) => {
    const newInputs = videoInputs.filter((_, i) => i !== index);
    const renumbered = newInputs.map((item, i) => ({ ...item, input_number: String(i + 1) }));
    setVideoInputs(renumbered);
    queueChange({ video_inputs: renumbered });
  };

  const copyVideoInput = (index) => {
    const itemToCopy = videoInputs[index];
    const newItem = {
      ...itemToCopy,
      id: `input-${Date.now()}`,
      input_number: String(index + 2),
    };
    const newInputs = [...videoInputs];
    newInputs.splice(index + 1, 0, newItem);
    const renumbered = newInputs.map((item, i) => ({ ...item, input_number: String(i + 1) }));
    setVideoInputs(renumbered);
    queueChange({ video_inputs: renumbered });
    toast.success('Input copied');
  };

  // Video Outputs
  const addVideoOutput = () => {
    const newItem = {
      id: `output-${Date.now()}`,
      output_number: String(videoOutputs.length + 1),
      description: '',
      source: '',
      resolution: '',
      video_connector: '',
      location: '',
      notes: ''
    };
    const newOutputs = [...videoOutputs, newItem];
    setVideoOutputs(newOutputs);
    queueChange({ video_outputs: newOutputs });
  };

  const updateVideoOutput = (index, field, value) => {
    const newOutputs = [...videoOutputs];
    newOutputs[index] = { ...newOutputs[index], [field]: value };
    setVideoOutputs(newOutputs);
    queueChange({ video_outputs: newOutputs });
  };

  const removeVideoOutput = (index) => {
    const newOutputs = videoOutputs.filter((_, i) => i !== index);
    const renumbered = newOutputs.map((item, i) => ({ ...item, output_number: String(i + 1) }));
    setVideoOutputs(renumbered);
    queueChange({ video_outputs: renumbered });
  };

  const copyVideoOutput = (index) => {
    const itemToCopy = videoOutputs[index];
    const newItem = {
      ...itemToCopy,
      id: `output-${Date.now()}`,
      output_number: String(index + 2),
    };
    const newOutputs = [...videoOutputs];
    newOutputs.splice(index + 1, 0, newItem);
    const renumbered = newOutputs.map((item, i) => ({ ...item, output_number: String(i + 1) }));
    setVideoOutputs(renumbered);
    queueChange({ video_outputs: renumbered });
    toast.success('Output copied');
  };

  // Assign stable IDs to items that don't have them (only on state change, not every render)
  useEffect(() => {
    if (!videoInputs || videoInputs.length === 0) return;
    let needsUpdate = false;
    const updatedInputs = videoInputs.map((item, i) => {
      if (!item) return { id: `input-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      if (!item.id) {
        needsUpdate = true;
        return { ...item, id: `input-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
    if (needsUpdate) setVideoInputs(updatedInputs);
  }, [videoInputs.length]);

  useEffect(() => {
    if (!videoOutputs || videoOutputs.length === 0) return;
    let needsUpdate = false;
    const updatedOutputs = videoOutputs.map((item, i) => {
      if (!item) return { id: `output-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      if (!item.id) {
        needsUpdate = true;
        return { ...item, id: `output-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
    if (needsUpdate) setVideoOutputs(updatedOutputs);
  }, [videoOutputs.length]);

  const confirmDelete = (type, index) => {
    const item = type === 'input' ? videoInputs[index] : videoOutputs[index];
    setDeleteConfirm({ open: true, type, index, item });
  };

  const removeItem = () => {
    if (deleteConfirm.index === null) return;
    
    if (deleteConfirm.type === 'input') {
      removeVideoInput(deleteConfirm.index);
    } else {
      removeVideoOutput(deleteConfirm.index);
    }
    
    setDeleteConfirm({ open: false, type: null, index: null, item: null });
    toast.success(`Video ${deleteConfirm.type} removed`);
    setTimeout(saveNow, 0);
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Monitor className="h-5 w-5 text-red-400" />
            Video
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overview */}
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Overview</Label>
          <Textarea
            value={videoOverview}
            onChange={(e) => handleOverviewChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Video system overview..."
            rows={3}
            className="bg-background/50"
            data-testid="video-overview-input"
          />
        </div>

        {/* Video Inputs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Video Inputs</Label>
            <Button type="button" variant="outline" size="sm" onClick={addVideoInput}>
              <Plus className="h-4 w-4 mr-1" /> Add Input
            </Button>
          </div>
          {videoInputs.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleInputDragEnd}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-[60px]">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead className="w-[100px]">Resolution</TableHead>
                      <TableHead className="w-[120px]">Connector</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={videoInputs.filter(i => i?.id).map(i => i.id)} strategy={verticalListSortingStrategy}>
                      {videoInputs.filter(i => i?.id).map((input, index) => (
                        <SortableRow key={input.id} id={input.id} className="border-border/50">
                          <TableCell className="w-[60px] text-center font-mono text-muted-foreground">
                            {input.input_number || index + 1}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.description || ''}
                              onChange={(e) => updateVideoInput(index, 'description', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Description"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.source || ''}
                              onChange={(e) => updateVideoInput(index, 'source', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Device"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={input.resolution || 'none'}
                              onValueChange={(v) => {
                                updateVideoInput(index, 'resolution', v === 'none' ? '' : v);
                                setTimeout(saveNow, 0);
                              }}
                            >
                              <SelectTrigger className="bg-background/50 h-8">
                                <SelectValue placeholder="Res" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select</SelectItem>
                                {RESOLUTIONS.map((r) => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={input.video_connector || 'none'}
                              onValueChange={(v) => {
                                updateVideoInput(index, 'video_connector', v === 'none' ? '' : v);
                                setTimeout(saveNow, 0);
                              }}
                            >
                              <SelectTrigger className="bg-background/50 h-8">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select</SelectItem>
                                {VIDEO_CONNECTORS.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.location || ''}
                              onChange={(e) => updateVideoInput(index, 'location', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Location"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.notes || ''}
                              onChange={(e) => updateVideoInput(index, 'notes', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Notes"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => copyVideoInput(index)} className="hover:bg-muted" title="Copy row">
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDelete('input', index)} className="hover:bg-destructive/10" title="Delete row">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            </DndContext>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No video inputs. Click "Add Input" to add one.</p>
          )}
        </div>

        {/* Video Outputs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Video Outputs</Label>
            <Button type="button" variant="outline" size="sm" onClick={addVideoOutput}>
              <Plus className="h-4 w-4 mr-1" /> Add Output
            </Button>
          </div>
          {videoOutputs.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOutputDragEnd}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-[60px]">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead className="w-[100px]">Resolution</TableHead>
                      <TableHead className="w-[120px]">Connector</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={videoOutputs.filter(o => o?.id).map(o => o.id)} strategy={verticalListSortingStrategy}>
                      {videoOutputs.filter(o => o?.id).map((output, index) => (
                        <SortableRow key={output.id} id={output.id} className="border-border/50">
                          <TableCell className="w-[60px] text-center font-mono text-muted-foreground">
                            {output.output_number || index + 1}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.description || ''}
                              onChange={(e) => updateVideoOutput(index, 'description', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Description"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.source || ''}
                              onChange={(e) => updateVideoOutput(index, 'source', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Device"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={output.resolution || 'none'}
                              onValueChange={(v) => {
                                updateVideoOutput(index, 'resolution', v === 'none' ? '' : v);
                                setTimeout(saveNow, 0);
                              }}
                            >
                              <SelectTrigger className="bg-background/50 h-8">
                                <SelectValue placeholder="Res" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select</SelectItem>
                                {RESOLUTIONS.map((r) => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                        </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={output.video_connector || 'none'}
                              onValueChange={(v) => {
                                updateVideoOutput(index, 'video_connector', v === 'none' ? '' : v);
                                setTimeout(saveNow, 0);
                              }}
                            >
                              <SelectTrigger className="bg-background/50 h-8">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select</SelectItem>
                                {VIDEO_CONNECTORS.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.location || ''}
                              onChange={(e) => updateVideoOutput(index, 'location', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Location"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.notes || ''}
                              onChange={(e) => updateVideoOutput(index, 'notes', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Notes"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => copyVideoOutput(index)} className="hover:bg-muted" title="Copy row">
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDelete('output', index)} className="hover:bg-destructive/10" title="Delete row">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            </DndContext>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No video outputs. Click "Add Output" to add one.</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Additional Notes</Label>
          <Textarea
            value={videoNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Additional video notes..."
            rows={3}
            className="bg-background/50"
            data-testid="video-notes-input"
          />
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, index: null, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video {deleteConfirm.type === 'input' ? 'Input' : 'Output'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this video {deleteConfirm.type}?
              {deleteConfirm.item && (
                <span className="block mt-2 font-medium text-foreground">
                  #{deleteConfirm.item.input_number || deleteConfirm.item.output_number || '?'}: {deleteConfirm.item.description || 'No description'}
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={removeItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableVideoTab;
